import Loader from './Utils/Loader';
import EventEmitter from './Utils/EventEmitter';

import body from '@models/player/Body.gltf';
import hand from '@models/player/hand.gltf';
import head from '@models/player/head.gltf';
import robot2body from '@models/robots/robot2body.gltf';
import robot2head from '@models/robots/robot2head.gltf';
import robot4body from '@models/robots/robot4body.gltf';
import robot4head from '@models/robots/robot2_head.gltf';
import robotHands from '@models/robots/robot_hands.gltf';

import settingsIcon from '@assets/settings-xxl.png';
import coruseIcon from '@assets/icons/defaultCourseIcon.png';
import arrowBottom from '@assets/icons/arrowBottom.png';
import arrowTop from '@assets/icons/arrowTop.png';
import rightArrow from '@assets/icons/icon-name-scroll-right-white-full.png';
import leftArrow from '@assets/icons/icon-name-scroll-left-white-full.png';
import circleIcon from '@assets/icons/circle-outline-128.png';
import speakerIcon from '@assets/icons/icons8-speaker-96.png';

import chatbot from '@models/robots/chatbot.gltf';
import lobbyScene from '@scenes/mainLobby.gltf';
import classScene from '@scenes/classroom.gltf';
import schoolScene from '@scenes/schoolLobby.gltf';
import groupScene from '@scenes/groupScene.gltf';
import privateRoom from '@scenes/privateSpace.gltf';
import helvetiker from '@models/rooms/helvetiker-regular-font.json';
import roboto_regular from '@models/rooms/Roboto_Regular.json';
import sketchBoard from '@models/sketchboardModel.gltf';

import smokeTexture from '@assets/textures/smoke.png';
import model3dPlatform from '@models/modelViewerPlatform.gltf';

import { singleton } from 'tsyringe';
import AxiosHttpClient from './Network/AxiosHttpClient';

@singleton()
export default class Resources extends EventEmitter {
    public loader: Loader;
    public items: { [key: string]: any } = {};
    private _isLoaded: boolean = false;
    private _loadingPromise: Promise<{ [key: string]: any }> | null = null;
    private _readyPromise: Promise<void> | null = null;
    private _readyResolve: (() => void) | null = null;

    public constructor(public httpClient: AxiosHttpClient) {
        super();

        this._readyPromise = new Promise((resolve) => {
            this._readyResolve = resolve;
        });
    }

    public get whenReady(): Promise<void> {
        return this._readyPromise;
    }


    public async loadAsync(): Promise<{ [key: string]: any }> {
        if (this._isLoaded) {
            return this.items;
        }

        if (this._loadingPromise) {
            return this._loadingPromise;
        }

        this._loadingPromise = this._performLoad();

        try {
            const result = await this._loadingPromise;
            this._isLoaded = true;

            await new Promise((resolve) => setTimeout(resolve, 0));

            return result;
        } catch (error) {
            this._loadingPromise = null;
            throw error;
        }
    }


    private async _performLoad(): Promise<{ [key: string]: any }> {
        this.loader = new Loader(this.httpClient);
        this.items = {};

        this.loader.on('fileEnd', (_resource: any, _data: any) => {
            this.items[_resource.name] = _data;

            const { loaded, toLoad } = this.loader;
            const percent = Math.round((loaded / toLoad) * 100);
            this.trigger('progress', [percent]);

            if (_resource.name === 'user') {
                console.log('📦 Resources: User data loaded:', {
                    hasUser: !!_data,
                    hasChatToken: !!_data?.chat_token,
                    userId: _data?.id,
                });
            }

            if (_resource.name === 'translations') {
                console.log('📦 Resources: Translations loaded:', {
                    hasTranslations: !!_data,
                    hasLabels: !!_data?.labels,
                    labelCount: _data?.labels ? Object.keys(_data.labels).length : 0,
                });
            }

            if (_resource.name === 'settings') {
                console.log('📦 Resources: Settings loaded:', {
                    hasSettings: !!_data,
                    firebaseDisabled: _data?.is_firebase_disabled,
                    hasFirebaseConfig: !!_data?.firebase_client_settings,
                });
            }
        });

        const resourcesToLoad = [
            { name: 'user', source: 'api/v2/user/', type: 'http' },
            { name: 'body', source: body },
            { name: 'hand', source: hand },
            { name: 'head', source: head },
            { name: 'settingsIcon', source: settingsIcon },
            { name: 'defaultCourseIcon', source: coruseIcon },
            { name: 'arrowBottom', source: arrowBottom },
            { name: 'arrowTop', source: arrowTop },
            { name: 'leftArrow', source: leftArrow },
            { name: 'rightArrow', source: rightArrow },
            { name: 'robot2body', source: robot2body },
            { name: 'robot2head', source: robot2head },
            { name: 'robot4body', source: robot4body },
            { name: 'robot4head', source: robot4head },
            { name: 'robotHands', source: robotHands },
            { name: 'smokeTexture', source: smokeTexture },
            {
                name: 'helvetiker',
                source: helvetiker,
                type: 'font',
            },
            {
                name: 'roboto_regular',
                source: roboto_regular,
                type: 'font',
            },
            { name: 'lobbyScene', source: lobbyScene },
            { name: 'classScene', source: classScene },
            { name: 'schoolScene', source: schoolScene },
            { name: 'groupScene', source: groupScene },
            { name: 'privateRoom', source: privateRoom },
            { name: 'circleIcon', source: circleIcon },
            { name: 'speakerIcon', source: speakerIcon },
            { name: 'chatbot', source: chatbot },
            { name: 'sketchBoard', source: sketchBoard },
            { name: 'model3dPlatform', source: model3dPlatform },
            {
                name: 'translations',
                source: (userData) => {
                    return `api/v2/translations/${userData.lang_id}`;
                },
                type: 'http',
                dependsOn: { resource: 'user' },
            },
            {
                name: 'translationsPL',
                source: `api/v2/translations/`,
                type: 'http',
            },
            {
                name: 'settings',
                source: '/api/v2/settings',
                type: 'http',
            },
            {
                name: 'events',
                source: '/api/v2/user/notifications',
                type: 'http',
            },
        ];

        try {
            const loadedItems = await this.loader.loadAsync(resourcesToLoad);
            this.items = { ...this.items, ...loadedItems };

            console.log('📦 Resources: All items loaded, validating...');

            const criticalResources = ['user', 'settings', 'translations'];
            const missing = criticalResources.filter((name) => !this.items[name]);

            if (missing.length > 0) {
                console.error('❌ Resources: Missing critical resources:', missing);
            } else {
                console.log('✅ Resources: All critical resources present');
            }

            this.trigger('ready');

            if (this._readyResolve) {
                this._readyResolve();
            }

            return this.items;
        } catch (error) {
            this.trigger('error', [error]);
            throw new Error(`Failed to load resources: ${error}`);
        }
    }


    public get isLoaded(): boolean {
        return this._isLoaded;
    }


    public get isLoading(): boolean {
        return this._loadingPromise !== null && !this._isLoaded;
    }


    public reset(): void {
        this._isLoaded = false;
        this._loadingPromise = null;
        this.items = {};
    }


    public getResource<T = any>(name: string): T | null {
        return this.items[name] || null;
    }


    public hasResource(name: string): boolean {
        return name in this.items;
    }


    public start(): void {
        this.loadAsync().catch((error) => {
            console.error('Error in Resources.start():', error);
        });
    }
}
